import * as React from 'react';

import { Icon } from '@rbilabs/components-library';
import { Box } from '@rbilabs/components-library/build/components/layout';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { ActionButton, ActionButtonVariants, ActionLink } from 'components/action-button';
import { useFeatureAggregatorRedirectButton } from 'hooks/use-feature-aggregator-redirect-button';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { routes } from 'utils/routing';

import {
  Container,
  ContainerThirdParties,
  DeliveryUnavailableHeaderContainer,
  LoadingText,
} from './delivery-unavailable.styled';
import { IDeliveryUnavailableProps } from './types';

/**
 *
 * DeliveryUnavailable informs the guest that the address they entered
 * is unable to receive delivery at this time
 *
 */

const StyledAddress = styled.p`
  text-transform: capitalize;
`;

export const DeliveryUnavailable = ({
  title,
  message,
  address,
  primaryButton,
  close,
}: IDeliveryUnavailableProps) => {
  const { formatMessage } = useIntl();
  const { activeButtons, isLoading } = useFeatureAggregatorRedirectButton();
  const redirectButtonsSet = !isLoading && !!activeButtons?.length;
  const enableDeliveryOnSameScreen = useFlag(
    LaunchDarklyFlag.ENABLE_DELIVERY_ADDRESS_ON_SAME_SCREEN
  );
  const onClickExternalButton = (e: React.SyntheticEvent, url: string) => {
    e.preventDefault();
    window.open(url, '_blank', 'noopener');
    close();
  };

  return (
    <Container
      data-testid="delivery-unavailable-for-address"
      $deliveryOnSameScreen={enableDeliveryOnSameScreen}
    >
      <DeliveryUnavailableHeaderContainer>
        <Icon icon="errorFilled" color="icon-default" width="16px" aria-hidden />
        {title}
      </DeliveryUnavailableHeaderContainer>
      <LoadingText $deliveryOnSameScreen={enableDeliveryOnSameScreen}>
        {message}

        {address && (
          <StyledAddress data-private data-dd-privacy="mask">
            {address}
          </StyledAddress>
        )}
      </LoadingText>
      {primaryButton && (
        <Box width="100%" minWidth="auto" minHeight="3rem" margin="8px 0" center>
          {primaryButton}
        </Box>
      )}
      <Box width="100%" minWidth="auto" minHeight="3rem" margin="8px 0" center>
        <ActionButton
          fullWidth
          data-testid="use-different-address"
          onClick={close}
          variant={primaryButton ? ActionButtonVariants.OUTLINE : ActionButtonVariants.PRIMARY}
        >
          {formatMessage({ id: 'useDifferentAddress' })}
        </ActionButton>
      </Box>
      {redirectButtonsSet && (
        <ContainerThirdParties>
          <p>{formatMessage({ id: 'visitMarketplaces' })}</p>
          {activeButtons.map((data, index) => (
            <Box
              width="100%"
              minWidth="auto"
              margin="8px 0"
              center
              key={`descriptionItem-${index}`}
            >
              <ActionLink
                data-testid={`delivery-partner-${data.name}`}
                fullWidth
                to={routes.storeLocator}
                onClick={e => onClickExternalButton(e, data.url)}
                variant={ActionButtonVariants.OUTLINE}
              >
                {data.buttonText}
              </ActionLink>
            </Box>
          ))}
        </ContainerThirdParties>
      )}
    </Container>
  );
};
